import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import {
  Search,
  UserCheck,
  MessageSquare,
  Calendar,
  Mail,
  KeyRound,
  Eye,
  EyeOff,
  X,
  UserCircle,
} from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { formatISTDate } from '../../../components/lib/dateUtils'
import { ChatModelVisibilityEditor } from './ChatModelVisibilityEditor'

interface UserData {
  id: string
  username: string
  email: string
  firstName?: string
  lastName?: string
  createdAt: string
  chatCount: number
  messageCount: number
  lastActive?: string
}

type SortOption = 'username' | 'createdAt' | 'chatCount' | 'messageCount' | 'lastActive'

interface ChangePasswordModalProps {
  user: UserData
  onClose: () => void
}

function ChangePasswordModal({ user, onClose }: ChangePasswordModalProps) {
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const res = await fetch(`${API_BASE_URL}admin/users/${user.id}/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ new_password: newPassword }),
      })
      if (res.ok) {
        setSuccess(true)
        setTimeout(onClose, 1200)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.detail || 'Failed to update password.')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Change Password</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              for <span className="font-medium text-indigo-600 dark:text-indigo-400">{user.username}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="flex items-center justify-center gap-2 py-6 text-green-600 dark:text-green-400 text-sm font-medium">
            Password updated successfully!
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                New Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setError('') }}
                  placeholder="Enter new password"
                  className="pr-10"
                  autoFocus
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex gap-3 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white hover:opacity-90"
                disabled={submitting || !newPassword}
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Updating…
                  </span>
                ) : 'Update Password'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

interface UserProfileModalProps {
  user: UserData
  onClose: () => void
}

function UserProfileModal({ user, onClose }: UserProfileModalProps) {
  const base = `${API_BASE_URL}admin/users/${user.id}/model-configuration`
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-8 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl w-full max-w-2xl mx-auto my-auto p-6 max-h-[calc(100vh-4rem)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xl font-bold flex-shrink-0">
              {user.username.substring(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {user.username}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-1">
                <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{user.email}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Joined {formatISTDate(user.createdAt)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <ChatModelVisibilityEditor
          title="Models in chat picker"
          description="Overrides global Model Configuration for this user only. Saving creates personal visibility rules; use “Use global defaults” to inherit the global list again."
          getUrl={base}
          putUrl={base}
          deleteUrl={base}
          className="max-w-none"
        />
      </div>
    </div>
  )
}

export function UsersManagement() {
  const [users, setUsers] = useState<UserData[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('lastActive')
  const [changePwUser, setChangePwUser] = useState<UserData | null>(null)
  const [profileUser, setProfileUser] = useState<UserData | null>(null)

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}admin/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        setUsers(data)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredUsers = users
    .filter(user => 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'username':
          return a.username.localeCompare(b.username)
        case 'createdAt':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        case 'chatCount':
          return b.chatCount - a.chatCount
        case 'messageCount':
          return b.messageCount - a.messageCount
        case 'lastActive': {
          if (a.lastActive && b.lastActive) {
            return new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime()
          } else if (a.lastActive && !b.lastActive) {
            return -1 // a has activity, b doesn't
          } else if (!a.lastActive && b.lastActive) {
            return 1 // b has activity, a doesn't
          } else {
            // Both have no activity, fall back to username
            return a.username.localeCompare(b.username)
          }
        }
        default:
          return 0
      }
    })

  return (
    <>
    <div className="space-y-6">
      {/* Search and Filter */}
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>Search and view all registered users</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search Users</Label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="search"
                  placeholder="Search by username or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-10 pl-10"
                />
              </div>
            </div>
            <div className="sm:w-48 ">
              <Label htmlFor="sort">Sort By</Label>
              <div className="relative mt-2">
                <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="username">Username</SelectItem>
                    <SelectItem value="createdAt">Join Date</SelectItem>
                    <SelectItem value="chatCount">Chat Count</SelectItem>
                    <SelectItem value="messageCount">Message Count</SelectItem>
                    <SelectItem value="lastActive">Last Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>All Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="space-y-4">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-start gap-4 mb-4 sm:mb-0">
                    <div className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
                      {user.username.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {user.username}
                        </p>
                        {user.lastActive && new Date(user.lastActive).toDateString() === new Date().toDateString() && (
                          <Badge variant="secondary" className="text-xs">
                            Active Today
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <Mail className="h-3 w-3" />
                        {user.email}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="h-3 w-3" />
                        Joined {formatISTDate(user.createdAt)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 sm:gap-6 items-center">
                    <button
                      type="button"
                      onClick={() => setProfileUser(user)}
                      className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 border border-indigo-200 dark:border-indigo-800 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                      title="View profile and model access"
                    >
                      <UserCircle className="h-3 w-3" />
                      View profile
                    </button>
                    <button
                      type="button"
                      onClick={() => setChangePwUser(user)}
                      className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 border border-indigo-200 dark:border-indigo-800 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                      title="Change password"
                    >
                      <KeyRound className="h-3 w-3" />
                      Password
                    </button>
                    <div className="text-center w-24 min-w-24">
                      <div className="flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <Calendar className="h-3 w-3" />
                        <span className="text-xs">Last Active</span>
                      </div>
                      {user.lastActive ? (
                        <div className="text-xs font-medium text-gray-900 dark:text-white leading-tight">
                          {formatISTDate(user.lastActive)}
                        </div>
                      ) : (
                        <p className="text-xs font-medium text-gray-900 dark:text-white">Never</p>
                      )}
                    </div>
                    <div className="text-center w-16 min-w-16">
                      <div className="flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <MessageSquare className="h-3 w-3" />
                        <span className="text-xs">Chats</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {user.chatCount}
                      </p>
                    </div>
                    <div className="text-center w-16 min-w-16">
                      <div className="flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <UserCheck className="h-3 w-3" />
                        <span className="text-xs">Messages</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {user.messageCount}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              {searchTerm ? 'No users found matching your search' : 'No users registered yet'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>

    {changePwUser && (
      <ChangePasswordModal
        user={changePwUser}
        onClose={() => setChangePwUser(null)}
      />
    )}
    {profileUser && (
      <UserProfileModal user={profileUser} onClose={() => setProfileUser(null)} />
    )}
    </>
  )
}

